import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  FormControlLabel
} from '@mui/material';
import {
  Add,
  Settings,
  Delete,
  Sync,
  CheckCircle,
  Error as ErrorIcon,
  Schedule
} from '@mui/icons-material';
import { format } from 'date-fns';
import api from '../../../../../utils/api';
import useShopifyStore from '../store/useShopifyStore';

const ConnectionsList = ({ onAddConnection, onSelectConnection }) => {
  const {
    connections,
    setConnections,
    loadingConnections,
    setLoadingConnections,
    removeConnection,
    updateConnection
  } = useShopifyStore();

  const [deleteDialog, setDeleteDialog] = useState({ open: false, connection: null });
  const [syncing, setSyncing] = useState({});
  const [error, setError] = useState('');

  useEffect(() => {
    loadConnections();
  }, []);

  const loadConnections = async () => {
    try {
      setLoadingConnections(true);
      const response = await api.get('/shopify/connections');
      if (response.data.success) {
        setConnections(response.data.data);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load connections');
    } finally {
      setLoadingConnections(false);
    }
  };

  const handleSync = async (connectionId) => {
    try {
      setSyncing({ ...syncing, [connectionId]: true });
      setError('');

      await api.post('/shopify/sync/products', {
        connectionId,
        direction: 'import'
      });

      // Reload connections to get updated sync status
      await loadConnections();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to sync');
    } finally {
      setSyncing({ ...syncing, [connectionId]: false });
    }
  };

  const handleToggleSync = async (connection) => {
    try {
      const response = await api.put(`/shopify/connections/${connection.id}`, {
        syncEnabled: !connection.syncEnabled
      });

      if (response.data.success) {
        updateConnection(connection.id, { syncEnabled: !connection.syncEnabled });
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update connection');
    }
  };

  const handleDelete = async () => {
    try {
      const { connection } = deleteDialog;
      await api.delete(`/shopify/connections/${connection.id}`);
      removeConnection(connection.id);
      setDeleteDialog({ open: false, connection: null });
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete connection');
    }
  };

  const getSyncStatusChip = (connection) => {
    if (syncing[connection.id]) {
      return <Chip icon={<CircularProgress size={16} />} label="Syncing" color="primary" size="small" />;
    }

    switch (connection.syncStatus) {
      case 'syncing':
        return <Chip icon={<Sync />} label="Syncing" color="primary" size="small" />;
      case 'error':
        return <Chip icon={<ErrorIcon />} label="Error" color="error" size="small" />;
      case 'idle':
        return <Chip icon={<CheckCircle />} label="Ready" color="success" size="small" />;
      default:
        return <Chip icon={<Schedule />} label="Pending" color="default" size="small" />;
    }
  };

  if (loadingConnections) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6">
          Connected Stores ({connections.length})
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={onAddConnection}
        >
          Add Connection
        </Button>
      </Box>

      {connections.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No Shopify Stores Connected
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Connect your Shopify store to start syncing products and inventory
            </Typography>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={onAddConnection}
            >
              Connect Your First Store
            </Button>
          </CardContent>
        </Card>
      ) : (
        <TableContainer component={Card}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Store</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Sync Enabled</TableCell>
                <TableCell>Last Sync</TableCell>
                <TableCell>Direction</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {connections.map((connection) => (
                <TableRow key={connection.id} hover>
                  <TableCell>
                    <Box>
                      <Typography variant="body1" fontWeight="medium">
                        {connection.shopName || connection.shopDomain}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {connection.shopDomain}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    {getSyncStatusChip(connection)}
                  </TableCell>
                  <TableCell>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={connection.syncEnabled}
                          onChange={() => handleToggleSync(connection)}
                          size="small"
                        />
                      }
                      label=""
                    />
                  </TableCell>
                  <TableCell>
                    {connection.lastSyncAt ? (
                      <Typography variant="body2">
                        {format(new Date(connection.lastSyncAt), 'MMM d, yyyy HH:mm')}
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        Never
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={connection.syncDirection.replace('_', ' → ')}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={() => handleSync(connection.id)}
                      disabled={syncing[connection.id]}
                      title="Sync Now"
                    >
                      <Sync />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => onSelectConnection(connection)}
                      title="Settings"
                    >
                      <Settings />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => setDeleteDialog({ open: true, connection })}
                      title="Delete"
                      color="error"
                    >
                      <Delete />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, connection: null })}
      >
        <DialogTitle>Delete Connection?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the connection to{' '}
            <strong>{deleteDialog.connection?.shopName}</strong>?
            This will stop all synchronization and remove all mappings.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, connection: null })}>
            Cancel
          </Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ConnectionsList;
